import { useMemo, useState } from "react";
import { Button, Empty, Segmented, Spin, Table, Tag } from "antd";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "@/components/UI/PageHeader";
import { useScopeParams } from "@/hooks/useScopeParams";
import definitionsApi from "@/services/definitionsApi";
import type { TrialDefinition } from "@/services/definitionsApi";

const SCOPE_LABEL: Record<string, { text: string; color?: string }> = {
  trial: { text: "This trial", color: "blue" },
  tenant: { text: "Tenant standard" },
  default: { text: "Vivo default" },
};

export default function TrialDefinitionsPage() {
  const { id: trialId, buildUrl } = useScopeParams();
  const navigate = useNavigate();
  const [family, setFamily] = useState("all");

  const definitionsQuery = useQuery({
    queryKey: ["trial-definitions", trialId],
    queryFn: () => definitionsApi.list(trialId as string),
    enabled: Boolean(trialId),
  });

  const data = definitionsQuery.data;
  const rows = useMemo(
    () =>
      (data?.definitions ?? []).filter(
        (row) => family === "all" || row.family === family,
      ),
    [data, family],
  );

  if (!trialId) return null;

  if (definitionsQuery.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin />
      </div>
    );
  }

  if (definitionsQuery.isError || !data) {
    return (
      <Empty
        description="Could not load what is governing this trial."
        className="mt-16"
      />
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-3 p-4">
      <PageHeader
        title="Definitions in force"
        tooltipText="Every rule and policy governing this trial, whichever discipline owns it. Read-only — each row links to where it is authored."
        dataTestId="trial-definitions-header"
      />

      <p className="m-0 max-w-[80ch] text-xs text-[#556C75]">
        Read-only. Definitions are authored by the discipline that owns them, so
        each row links to its home rather than being editable here.
      </p>

      <Segmented
        value={family}
        onChange={(value) => setFamily(String(value))}
        options={[
          { label: "All", value: "all" },
          ...Object.entries(data.families).map(([key, meta]) => ({
            label: meta.label,
            value: key,
          })),
        ]}
      />

      <Table<TrialDefinition>
        dataSource={rows}
        rowKey={(row) => `${row.family}-${row.name}`}
        size="small"
        pagination={false}
        scroll={{ x: true }}
        columns={[
          {
            title: "Definition",
            dataIndex: "name",
            render: (name: string, row) => (
              <div className="flex flex-col">
                <span className="text-[#2B4752]">{name}</span>
                <span className="text-[11px] text-[#8FA5A0]">
                  {data.families[row.family]?.owner}
                </span>
              </div>
            ),
          },
          { title: "Applies to", dataIndex: "subject" },
          {
            title: "In force",
            dataIndex: "summary",
            render: (summary: string) => (
              <span className="text-xs text-[#556C75]">{summary}</span>
            ),
          },
          {
            title: "Scope",
            dataIndex: "scope",
            render: (scope: string, row) => (
              <span className="whitespace-nowrap">
                <Tag color={SCOPE_LABEL[scope]?.color}>
                  {SCOPE_LABEL[scope]?.text ?? scope}
                </Tag>
                {row.version !== null && (
                  <span className="text-xs tabular-nums text-[#8FA5A0]">
                    v{row.version}
                  </span>
                )}
              </span>
            ),
          },
          {
            title: "Since",
            dataIndex: "effective_from",
            render: (value: string | null) => (
              <span className="text-xs whitespace-nowrap text-[#556C75]">
                {value ? new Date(value).toLocaleDateString() : "—"}
              </span>
            ),
          },
          {
            title: "Last changed by",
            dataIndex: "author",
            render: (author: string | null, row) => (
              <div className="flex flex-col">
                <span className="text-xs text-[#556C75]">{author ?? "—"}</span>
                {row.reason_for_change && (
                  <span className="text-[11px] text-[#8FA5A0]">
                    {row.reason_for_change}
                  </span>
                )}
              </div>
            ),
          },
          {
            title: "",
            key: "home",
            render: (_: unknown, row) => (
              <Button
                size="small"
                type="link"
                onClick={() =>
                  navigate(buildUrl(data.families[row.family].route))
                }
              >
                Open
              </Button>
            ),
          },
        ]}
      />
    </div>
  );
}
